import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { jlocation_name_code } from '../../interfaces/jlocation.interface';
import { HelperService } from '../../services/helper.service';
import { JSettings, SupaService } from '../../services/supa.service';
import { debounceTime } from 'rxjs/operators';
import { IframeWrapperComponent } from '../../components/iframewrapper/iframewrapper.component';
import { OSBSearchDialog } from './osbsearch/osbsearch.component';
import { Vehicle } from '../../interfaces/vehicle.interface';
import { ConfirmDelete } from '../../decorators/confirmdelete.decorator';
import { DmsService } from '../../services/dms.service';
import { environment } from '../../../environments/environment';
import { GeminisearchDialog } from './geminisearch/geminisearch.component';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {

  @ViewChild('strokeGroup') strokeGroup;

  loadingComplete = false;
  onlyMyItems = '';
  jlocations: jlocation_name_code[] | undefined = undefined;
  newRecords: VhcRecordHomeCard[] = [];
  preRecords: VhcRecordHomeCard[] = [];
  mpiRecords: VhcRecordHomeCard[] = [];
  jpRecords: VhcRecordHomeCard[] = [];
  authRecords: VhcRecordHomeCard[] = [];
  qiRecords: VhcRecordHomeCard[] = [];
  finRecords: VhcRecordHomeCard[] = [];
  columnNames: object = {}

  EmptyLists = localStorage.getItem('HomeEmptyLists') !== null;

  SAList: { id: string, name: string }[] = [];

  jlocation_codes: string[] = [];

  helpurl = 'https://www.youtube.com/embed/kPZH75Zja50';

  dataSubscription: Subscription;
  timerSubscription: Subscription;
  getNewData = new Subject();

  exconfig = this.supa.ExternalConfig();

  constructor(
    private helper: HelperService,
    private router: Router,
    private supa: SupaService,
    private loadingController: LoadingController,
    private dms: DmsService) { }

  ngOnInit() {
    this.supa.supabase.from('iqlocation').select('code,name,id').then(async loc => {
      if (loc.error) {
        this.helper.notify('Could not load locations', 'danger');
      } else {
        if (this.supa.LoginObject.value?.permissions.includes('VhcAllLocations')) {
          this.jlocations = loc.data;
        } else {
          this.jlocations = loc.data.filter(d => this.supa.LoginObject.value?.permissions.includes('Location' + d.id));
        }
        let sa_query = this.supa.supabase
          .from('employee_iqlocation_view')
          .select('name,id')
          .eq('receive_customers', true)
          .order("name");
        let locations = this.supa.LoginObject.value?.permissions.filter((permission)=>permission.startsWith("Location")).map((permission)=>permission.replace(/^Location/, ""));
        if (locations.length>0){
          sa_query = sa_query.in("iqlocation_id",locations)
        }
        this.SAList = (await sa_query).data ?? [];
        this.getNewData.next();
      }
    });
    this.dataSubscription = this.getNewData.pipe(debounceTime(500)).subscribe(v => { this.loadData(); });
    this.timerSubscription = timer(60000, 60000).subscribe(v => { this.getNewData.next(); });
    this.supa.supabase.from("vhc_vhcblueprint").select("naming").then(x => {
      const getColumnName = (o: any[], token: string) => ([...new Set(o.map(x => x[token].name))]).length == 1 ? o[0][token].name : [...new Set(o.map(x => x[token].abbreviation))].join(",");
      const blueprints = x.data.map(x => x.naming)
      this.columnNames['Pre'] = getColumnName(blueprints, "Pre") || "Pre check";
      this.columnNames['MPI'] = getColumnName(blueprints, "MPI") || "Multi point inspection";
      this.columnNames['QI'] = getColumnName(blueprints, "QI") || "Quality Inspection";
    })
    setTimeout(() => {
      this.onlyMyItems = this.supa.LoginObject.value?.id ?? '';
    }, 500)
  }

  ngOnDestroy() {
    if (this.EmptyLists) {
      localStorage.setItem('HomeEmptyLists', 'true')
    } else {
      localStorage.removeItem('HomeEmptyLists')
    }
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  selectedLocations(_: any) {
    this.getNewData.next();
  }

  getLocationFilter() {
    let locationFilter = this.jlocation_codes;
    if (locationFilter.length == 0) {
      locationFilter = (this.jlocations ? this.jlocations : []).map(l => l.code);
      locationFilter.push('');
    }
    return locationFilter;
  }

  filterText = ''

  filterTextUpdated() {
    this.getNewData.next();
  }

  recordIsFiltered(record: VhcRecordHomeCard) {
    return (record.cartype?.toLowerCase().indexOf(this.filterText) > -1
      || record.jlocation_code?.toLowerCase().indexOf(this.filterText) > -1
      || record.licenseplate?.toLowerCase().indexOf(this.filterText) > -1);
  }

  async syncVehicleAndOpenRecord(item: VhcRecordHomeCard) {
    const jc = await this.supa.supabase.from('jobcard').select('customer_name,customer_phone,customer_email,vehicle_year_of_build,vehicle_engine_code').eq('id', item.id).limit(1).single();
    if (!jc.data) {
      return;
    }
    const vehicle = await this.supa.supabase.from('vhc_vehicle').upsert({
      vin: item.vin!,
      licenseplate: item.licenseplate,
      cartype: item.cartype,
      enginecode: jc.data.vehicle_engine_code,
      yearofbuild: jc.data.vehicle_year_of_build,
      communicatewithowner: true,
      drivername: jc.data.customer_name,
      drivermail: jc.data.customer_email,
      driverphone: jc.data.customer_phone,
      ownername: jc.data.customer_name,
      ownermail: jc.data.customer_email,
      ownerphone: jc.data.customer_phone,
    }).eq('vin', item.vin);
    this.helper.createNewRecord(item.vin, item.id, item.jlocation_code);
  }

  async loadData() {
    console.log('loading data');

    this.loadingComplete = false;
    const locationFilter = this.getLocationFilter();
    const settings = await this.supa.Settings();

    const _preRecords: VhcRecordHomeCard[] = [];
    const _mpiRecords: VhcRecordHomeCard[] = [];
    const _jpRecords: VhcRecordHomeCard[] = [];
    const _authRecords: VhcRecordHomeCard[] = [];
    const _qiRecords: VhcRecordHomeCard[] = [];
    const _finRecords: VhcRecordHomeCard[] = [];
    let home_data_query =  this.supa.supabase
      .from('vhc_vhcrecord')
      .select('id,status,assignees,jlocation_code,portalstatus,vin,created_at,completedpre,completedprejp,completedpreauth,completedmpi,completedjp,completedauth,completedqi,vhc_vehicle(licenseplate,cartype)')
      .eq('deactivated', false)
      .eq('isdone', false)
      .neq('status', 'fin')
    let missing_vhc_query = this.supa.supabase
      .from('jobcard')
      .select('id,vehicle_car_type,vehicle_license_plate,vehicle_vin,iqlocation(code),vhc_vhcrecord(jobcard_id)')
      .is('vhc_vhcrecord', null)
    let locations = this.supa.LoginObject.value?.permissions.filter((permission)=>permission.startsWith("Location")).map((permission)=>permission.replace(/^Location/, ""));
    if (locations.length>0){
      
      home_data_query = home_data_query.in("jlocation_code",locations);
      missing_vhc_query = missing_vhc_query.in("iqlocation_id",locations);
    }

    const homeData = (await home_data_query).data;
    const missingVhc = (await missing_vhc_query).data;

    this.newRecords = missingVhc.map(jc => {
      return {
        assignees: [],
        cartype: jc.vehicle_car_type || '-',
        jlocation_code: Array.isArray(jc.iqlocation) ? jc.iqlocation.length > 0 ? jc.iqlocation[0].code : '-' : (jc.iqlocation as any)?.code ?? '-',
        licenseplate: jc.vehicle_license_plate,
        portalstatus: 0,
        vin: jc.vehicle_vin,
        status: 'new',
        stale: false,
        id: jc.id,
      } as VhcRecordHomeCard
    });

    const onlyme = (this.onlyMyItems === 'All items' ? '' : this.onlyMyItems)
    // PILOT locationFilter and only me
    const now = DateTime.local();
    if (homeData === null) {
      return
    }
    (homeData as VhcRecordHomeCard[] | any[]).filter(rec => this.filterText.length === 0 || this.recordIsFiltered(rec)).forEach(rec => {
      rec.licenseplate = Array.isArray(rec.vhc_vehicle[0]) ? (rec as any).vhc_vehicle[0].licenseplate : rec.vhc_vehicle.licenseplate;
      rec.cartype = Array.isArray(rec.vhc_vehicle[0]) ? (rec as any).vhc_vehicle[0].cartype : rec.vhc_vehicle.cartype;
      switch (rec.status) {
        case 'pre':
          rec.stale = DateTime.fromISO(rec.created_at).plus({ minutes: settings.maxtimepre }) < now;
          _preRecords.push(rec);
          break;
        case 'mpi':
          rec.stale = DateTime.fromISO(rec.completedpreauth).plus({ minutes: settings.maxtimempi }) < now;
          _mpiRecords.push(rec);
          break;
        case 'jp':
          rec.stale = DateTime.fromISO(rec.completedprejp != null ? rec.completedmpi : rec.completedpre).plus({ minutes: rec.completedprejp != null ? settings.maxtimejp : settings.maxtimeprejp }) < now;
          _jpRecords.push(rec);
          break;
        case 'auth':
          rec.stale = DateTime.fromISO(rec.completedpreauth != null ? rec.completedjp : rec.completedprejp).plus({ minutes: rec.completedpreauth != null ? settings.maxtimeauth : settings.maxtimepreauth }) < now;
          _authRecords.push(rec);
          break;
        case 'qi':
          rec.stale = DateTime.fromISO(rec.completedauth).plus({ minutes: settings.maxtimeqi }) < now;
          _qiRecords.push(rec);
          break;
        default:
          rec.stale = false;
          _finRecords.push(rec);
          break;
      }
    });
    this.preRecords = _preRecords.sort(a => -a.stale);
    this.mpiRecords = _mpiRecords.sort(a => -a.stale);
    this.jpRecords = _jpRecords.sort(a => -a.stale);
    this.authRecords = _authRecords.sort(a => -a.stale);
    this.qiRecords = _qiRecords.sort(a => -a.stale);
    this.finRecords = _finRecords.sort(a => -a.stale);

    this.loadingComplete = true;
  }

  openRecord(id) {
    this.router.navigate(['vhc-record', { id }]);
  }

  sgMouseIsDown = false;
  sgStartingX = 0;
  sgStartingScrollX = 0;
  setMouseDown(event, isDown: boolean) {
    if (isDown && this.strokeGroup) {
      this.sgStartingX = event.screenX;
      this.sgStartingScrollX = this.strokeGroup.nativeElement.scrollLeft;
      this.sgMouseIsDown = true;
    } else {
      this.sgMouseIsDown = false;
    }
  }

  sgMove(event) {
    if (this.sgMouseIsDown) {
      const deltaScrollX = this.sgStartingX - event.screenX;
      this.strokeGroup.nativeElement.scrollLeft = this.sgStartingScrollX + deltaScrollX;
    }
  }
}

export interface VhcRecordHomeCard {
  id: number;
  status: string;
  licenseplate: string;
  assignees: any;
  cartype: string;
  jlocation_code: string;
  portalstatus: number;
  vin?: string;
  osbtime?: number;
  stale: boolean;

  created_at?: string;
  completedpre?: string;
  completedprejp?: string;
  completedpreauth?: string;
  completedmpi?: string;
  completedjp?: string;
  completedauth?: string;
  completedqi?: string;
}

export interface OsbJob {
  appointmentId: number;
  customerTooltip: string;
  duration: number;
  id: number;
  name: string;
  operationCode: string;
  price: string;
}

export interface OsbAppointment {
  appointmentCancelled: boolean;
  appointmentCompleted: boolean;
  appointmentCompletedLate: boolean;
  arrivalTime: number;
  arrivalTimeChosen: string;
  brand: string;
  cancelDate?: any;
  creationTextSent: number;
  customerAddress?: any;
  customerCompany?: any;
  customerEmail?: any;
  customerName: string;
  customerPostalCode?: any;
  fromCustomer: boolean;
  handoverTime: number;
  hhaAutoRemark?: any;
  hhaNote: string;
  hhaService?: any;
  hhapayload: string;
  id: number;
  jobs: OsbJob[];
  kms?: any;
  language: string;
  licensePlate: string;
  locationCode: string;
  model: string;
  phoneNumber: string;
  receptionist: string;
  reminderTextSent: number;
  seenFolder: boolean;
  vin: string;
  withUser: string;
}
